import { useEffect, useState } from "react";
import { teacherApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  GraduationCap, Users, FlaskConical, PlayCircle, MessageSquareWarning,
  ChevronLeft, ChevronRight, X, Sparkles,
} from "lucide-react";

/**
 * Onboarding tour — 5-step brut-styled modal shown on the FIRST login of a
 * newly-registered teacher. Trigger conditions (all must be true):
 *   - GET /api/auth/me returns onboarding_completed_at === null
 *   - teacher.role === "teacher" (admins skip)
 *
 * On finish OR skip we POST /api/auth/complete-onboarding which is
 * server-side idempotent — subsequent logins never re-open the tour.
 * The user can also close the modal (X or ESC / backdrop click) which
 * also marks it completed, to avoid pestering on every reload.
 */
const buildSteps = (poolSize) => [
  {
    icon: Sparkles,
    title: "Witaj w Kartkówkach!",
    color: "#FEF08A",
    body: (
      <>
        <p className="mb-2">
          Cieszymy się, że jesteś. Twoja aplikacja jest gotowa — pokażę Ci
          <b> 4 najważniejsze funkcje</b> w 60 sekund.
        </p>
        <p className="text-sm text-[#64748B]">
          Po drodze możesz w każdej chwili nacisnąć „Pomiń&rdquo;.
        </p>
      </>
    ),
  },
  {
    icon: Users,
    title: "Krok 1 — Klasy",
    color: "#BFDBFE",
    body: (
      <>
        <p className="mb-2">
          Utwórz klasę (np. „3A Chemia rozszerzona&rdquo;) — to Twój
          <b> wirtualny dziennik</b>. Uczniowie NIE mają kont — dołączają
          jednorazowo do sesji przez kod.
        </p>
        <p className="text-sm text-[#64748B]">
          Menu: <b>Klasy</b> → <b>+ Nowa klasa</b>
        </p>
      </>
    ),
  },
  {
    icon: FlaskConical,
    title: "Krok 2 — Pytania",
    color: "#BBF7D0",
    body: (
      <>
        <p className="mb-2">
          Masz do dyspozycji <b>
            {poolSize != null
              ? `ponad ${Math.floor(poolSize / 100) * 100} gotowych pytań`
              : "ponad 2000 gotowych pytań"}
          </b>{" "}z chemii (podstawa 1-3 + rozszerzenie 1-4). Możesz też
          <b> dodawać własne</b>.
        </p>
        <p className="text-sm text-[#64748B]">
          Menu: <b>Pytania</b> → filtruj po zakresie / temacie
        </p>
      </>
    ),
  },
  {
    icon: PlayCircle,
    title: "Krok 3 — Sesje na żywo",
    color: "#FCA5A5",
    body: (
      <>
        <p className="mb-2">
          Wybierz klasę + pytania + limit czasu → uruchom sesję →
          <b> wyświetl kod </b>na projektorze. Uczniowie wpisują kod w
          telefonie i rozwiązują. Ty widzisz wyniki na żywo.
        </p>
        <p className="text-sm text-[#334155] mb-1">
          <b>3 tryby doboru pytań</b> (zakładki w oknie „Nowa kartkówka"):
        </p>
        <ul className="text-sm text-[#334155] list-disc pl-5 space-y-0.5">
          <li><b>🎲 Automatyczny</b> — losowanie z rozkładem per temat</li>
          <li><b>🔀 Cross-scope</b> — pytania z różnych klas w jednym teście</li>
          <li><b>✋ Ręczny wybór</b> — sam zaznaczasz pytania z listy</li>
        </ul>
        <p className="text-sm text-[#334155] mt-2 mb-1">
          <b>Opcje anti-ściąganie</b>:
        </p>
        <ul className="text-sm text-[#334155] list-disc pl-5 space-y-0.5">
          <li><b>🎯 Losuj kolejność</b> — każdy uczeń ma inne numery</li>
          <li><b>👥 Grupy A/B</b> — dwa kody + różne pule, wspólne statystyki</li>
          <li className="text-[#065F46]"><b>💡 Możesz łączyć obie</b> = maksymalna ochrona</li>
        </ul>
        <p className="text-sm text-[#64748B] mt-1 italic">
          W trybie Ręczny + A/B pojawią się dwa taby, by wybrać pytania osobno dla każdej grupy.
        </p>
        <p className="text-sm text-[#334155] mt-2">
          Po czasie kartkówka <b>kończy się sama</b> i auto-oddaje nieoddane prace.
        </p>
        <p className="text-sm text-[#64748B] mt-2">
          Menu: <b>Sesje</b> → <b>+ Nowa sesja</b>
        </p>
      </>
    ),
  },
  {
    icon: MessageSquareWarning,
    title: "Krok 4 — Feedback (BETA)",
    color: "#DDD6FE",
    body: (
      <>
        <p className="mb-2">
          Coś nie działa? Czegoś brakuje? Kliknij <b>żółty przycisk
          „Feedback&rdquo;</b> w prawym dolnym rogu — Twoje zgłoszenia
          czytamy i odpowiadamy osobiście.
        </p>
        <p className="text-sm text-[#64748B]">
          Dostępne z każdej strony aplikacji.
        </p>
      </>
    ),
  },
];

const LS_KEY = "onboarding_completed_v1";

export default function OnboardingTour() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [checked, setChecked] = useState(false);
  const [poolSize, setPoolSize] = useState(null);

  // Decide whether to open the tour after mount.
  // Server (`onboarding_completed_at`) is the source of truth.
  // LocalStorage is used ONLY as a safety-net between server call landing
  // and the completion POST — never as a primary gate. This avoids the
  // "tour disappeared and never came back" bug reported by user iter50.
  useEffect(() => {
    let cancelled = false;
    teacherApi.get("/auth/me").then(({ data }) => {
      if (cancelled) return;
      const serverSaysDone = !!data?.onboarding_completed_at;
      if (data?.role === "teacher" && !serverSaysDone) {
        setOpen(true);
        // Fetch live pool size for the "Krok 2 — Pytania" copy
        teacherApi.get("/questions/pool-size")
          .then(({ data: pd }) => setPoolSize(pd?.total))
          .catch(() => {});
      } else if (serverSaysDone) {
        // Sync LS so this browser matches server
        localStorage.setItem(LS_KEY, "1");
      }
      setChecked(true);
    }).catch(() => setChecked(true));
    return () => { cancelled = true; };
  }, []);

  const complete = async () => {
    setOpen(false);
    // Set LS FIRST so if the network POST fails we don't re-open on this
    // page load (safety-net only — next reload trusts server).
    localStorage.setItem(LS_KEY, "1");
    try {
      await teacherApi.post("/auth/complete-onboarding");
    } catch {
      // Non-critical — server-side is idempotent, LS prevents re-appearance
      // on THIS load. Next login the server will show tour again if the
      // POST truly never landed — acceptable trade-off.
    }
  };

  // ESC to skip. STEPS is always length 5 so we hard-code the bound
  // (avoids referencing the render-scope STEPS variable from inside effect).
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") complete();
      if (e.key === "ArrowRight" && step < 4) setStep(step + 1);
      if (e.key === "ArrowLeft" && step > 0) setStep(step - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step]);

  if (!checked || !open) return null;

  const STEPS = buildSteps(poolSize);
  const current = STEPS[step];
  const Icon = current.icon;
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      onClick={complete}
      data-testid="onboarding-backdrop"
    >
      <div
        className="brut border-4 border-[#0F172A] bg-white rounded-md max-w-lg w-full shadow-[10px_10px_0_#0F172A]"
        onClick={(e) => e.stopPropagation()}
        data-testid="onboarding-modal"
      >
        {/* Header */}
        <div
          className="border-b-4 border-[#0F172A] p-6 flex items-start gap-4"
          style={{ background: current.color }}
        >
          <div className="brut border-2 border-[#0F172A] rounded-md bg-white p-2 shrink-0">
            <Icon className="w-6 h-6" strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-black uppercase tracking-wider text-[#64748B]">
              {step + 1} / {STEPS.length}
            </div>
            <h2
              className="font-display font-black text-2xl"
              data-testid="onboarding-title"
            >
              {current.title}
            </h2>
          </div>
          <button
            onClick={complete}
            className="brut brut-hover bg-white border-2 border-[#0F172A] rounded-md p-1"
            title="Pomiń tour"
            data-testid="onboarding-close"
          >
            <X className="w-5 h-5" strokeWidth={2.5} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 min-h-[140px]" data-testid="onboarding-body">
          {current.body}
        </div>

        {/* Progress dots */}
        <div className="flex justify-center gap-1.5 pb-4">
          {STEPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setStep(i)}
              className={`w-2.5 h-2.5 rounded-full brut ${
                i === step ? "bg-[#0F172A]" : "bg-[#CBD5E1]"
              }`}
              aria-label={`Krok ${i + 1}`}
              data-testid={`onboarding-dot-${i}`}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="border-t-2 border-[#0F172A] p-4 flex justify-between gap-2 bg-[#F8FAFC]">
          <Button
            onClick={() => setStep(step - 1)}
            disabled={isFirst}
            className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md"
            data-testid="onboarding-prev"
          >
            <ChevronLeft className="w-4 h-4 mr-1" strokeWidth={2.5} />
            Wstecz
          </Button>
          <div className="flex gap-2">
            <Button
              onClick={complete}
              className="brut brut-hover bg-white border-2 border-[#0F172A] font-bold rounded-md text-[#64748B]"
              data-testid="onboarding-skip"
            >
              Pomiń
            </Button>
            {isLast ? (
              <Button
                onClick={complete}
                className="brut brut-hover bg-[#FEF08A] border-2 border-[#0F172A] font-black rounded-md"
                data-testid="onboarding-finish"
              >
                <GraduationCap className="w-4 h-4 mr-2" strokeWidth={2.5} />
                Zaczynam!
              </Button>
            ) : (
              <Button
                onClick={() => setStep(step + 1)}
                className="brut brut-hover bg-[#0F172A] text-white border-2 border-[#0F172A] font-bold rounded-md"
                data-testid="onboarding-next"
              >
                Dalej
                <ChevronRight className="w-4 h-4 ml-1" strokeWidth={2.5} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
